import { useState } from "react";

import { HUB_PERMISSOES, usePermissoes } from "@/modules/permissoes";
import { useAtivarDireto } from "../hooks/use-ativar-direto";
import { ativarAlvo, avisoDaAtivacao } from "./base-dados.ativacao";
import {
  BASE_DADOS_ATIVAR_LABEL,
  BASE_DADOS_AVISO_TIPOS_OBRA,
  BASE_DADOS_CATEGORIAS,
  BASE_DADOS_INATIVAR_LABEL,
  BASE_DADOS_SITUACAO_ATIVO,
  BASE_DADOS_SITUACAO_INATIVO,
  COLUNAS_POR_CATEGORIA,
  DESCRICAO_POR_CATEGORIA,
  ERROS,
} from "./base-dados.constants";
import type { BaseDadosCategoria } from "./base-dados.constants";
import {
  filtrarInativos,
  montarLinhasEstrutura,
  montarLinhasTiposObra,
  ordenarLinhas,
  proximaOrdenacao,
} from "./base-dados.linhas";
import type { AcaoBase, AlvoSituacao, LinhaBase, Ordenacao } from "./base-dados.linhas";
import { useBaseDadosDados } from "./hooks/use-base-dados-dados";
import type { BaseDadosConteudo, BaseDadosLinha, BaseDadosViewModel } from "./types-base-dados";

function rotuloAcao({ tipo, alvo, nome }: AcaoBase) {
  if (tipo === "inativar") return `${BASE_DADOS_INATIVAR_LABEL} ${nome}`;
  const arrasta = alvo.tipo === "grupo" && alvo.comSubgrupos;
  return `${BASE_DADOS_ATIVAR_LABEL} ${nome}${arrasta ? " e subgrupos" : ""}`;
}

export function useBaseDados(): BaseDadosViewModel {
  const { hasPermissao } = usePermissoes();
  const podeEditar = hasPermissao(HUB_PERMISSOES.FINANCEIRO_EDIT_CONFIGURACAO);
  const [categoria, setCategoria] = useState<BaseDadosCategoria>("ESTRUTURA");
  const [mostrarInativos, setMostrarInativos] = useState(false);
  const [ordenacao, setOrdenacao] = useState<Ordenacao | null>(null);
  const [acaoPendente, setAcaoPendente] = useState<BaseDadosViewModel["acaoPendente"]>(null);

  const dados = useBaseDadosDados(mostrarInativos);
  const linhasPorCategoria: Record<BaseDadosCategoria, LinhaBase[]> = {
    ESTRUTURA: filtrarInativos(montarLinhasEstrutura(dados.estrutura.itens), mostrarInativos),
    TIPOS_OBRA: montarLinhasTiposObra(dados.tiposObra.itens),
  };
  const fontes = { ESTRUTURA: dados.estrutura, TIPOS_OBRA: dados.tiposObra };
  const fonte = fontes[categoria];

  const recarregarListas = () => {
    dados.estrutura.recarregar();
    dados.tiposObra.recarregar();
  };
  const ativarDireto = useAtivarDireto<AlvoSituacao>({
    enviar: ativarAlvo,
    sucesso: avisoDaAtivacao,
    aoConcluir: recarregarListas,
    erroPadrao: ERROS.generico(),
  });
  const abrirAcao = (acao: AcaoBase) => {
    if (acao.tipo === "ativar") void ativarDireto(acao.alvo);
    else setAcaoPendente({ ...acao, tipo: "inativar" });
  };

  const linhas: BaseDadosLinha[] = ordenarLinhas(linhasPorCategoria[categoria], ordenacao).map(
    (linha) => ({
      id: linha.id,
      celulas: linha.celulas,
      inativa: linha.situacao === "INATIVO",
      situacao:
        linha.situacao === "ATIVO" ? BASE_DADOS_SITUACAO_ATIVO : BASE_DADOS_SITUACAO_INATIVO,
      acao: podeEditar
        ? {
            label: rotuloAcao(linha.acao),
            testId: `base-dados-acao-${linha.id}`,
            executar: () => abrirAcao(linha.acao),
          }
        : undefined,
    }),
  );

  let conteudo: BaseDadosConteudo = "lista";
  if (fonte.semPermissao) conteudo = "sem-permissao";
  else if (fonte.erro) conteudo = "erro";
  else if (fonte.carregando && linhas.length === 0) conteudo = "carregando";
  else if (linhas.length === 0) conteudo = "vazio";

  const selecionar = (proxima: BaseDadosCategoria) => {
    setCategoria(proxima);
    setOrdenacao(null);
  };

  return {
    conteudo,
    erro: fonte.erro,
    menu: BASE_DADOS_CATEGORIAS.map(({ valor, rotulo }) => ({
      categoria: valor,
      rotulo,
      ativo: valor === categoria,
      contador: fontes[valor].carregando ? null : linhasPorCategoria[valor].length,
      testId: `base-dados-menu-${valor}`,
      selecionar: () => selecionar(valor),
    })),
    categoria,
    descricao: DESCRICAO_POR_CATEGORIA[categoria],
    aviso: categoria === "TIPOS_OBRA" ? BASE_DADOS_AVISO_TIPOS_OBRA : null,
    colunas: COLUNAS_POR_CATEGORIA[categoria],
    linhas,
    ordenacao,
    ordenarPor: (campo) => setOrdenacao((atual) => proximaOrdenacao(atual, campo)),
    filtraInativos: categoria === "ESTRUTURA",
    mostrarInativos,
    alternarInativos: () => setMostrarInativos((atual) => !atual),
    recarregar: fonte.recarregar,
    novoGrupo:
      podeEditar && categoria === "ESTRUTURA"
        ? () => setAcaoPendente({ tipo: "novo-grupo" })
        : undefined,
    novoGrupoDesabilitado: !dados.tiposObra.pronta,
    tiposObra: dados.tiposObra.itens,
    acaoPendente,
    fecharAcao: () => setAcaoPendente(null),
    concluirAcao: () => {
      setAcaoPendente(null);
      recarregarListas();
    },
  };
}
